// Derives the dashboard onboarding progress from the aggregated onboarding-status
// payload (Redux `state.app.onboardingStatus`), fetched from the backend on entry.
import { useSelector } from "react-redux";

import {
  REQUIRED_STEPS,
  RECOMMENDED_STEPS,
  isDone,
  verificationPending,
  requiredDoneCount,
  allRequiredDone,
  reachedStepIds,
  MENU_STEP_GATE,
  ROUTE_STEP_GATE,
  PRESETS
} from "./onboarding.model";

// Pure status builder — shared by the hook and previews/tests.
// `status` = OnboardingStatusResponse payload; `activeBranch` (Redux) is used only
// for CTA deep-links and the branch name shown in the copy.
export const buildOnboardingStatus = (
  status = {},
  activeBranch,
  isDevOverride = false
) => {
  // First not-done required step is "active"; earlier are done, later are locked.
  const doneFlags = REQUIRED_STEPS.map(step => isDone(step.id, status));
  const activeIndex = doneFlags.findIndex(done => !done);

  const steps = REQUIRED_STEPS.map((step, index) => {
    const done = doneFlags[index];
    const pending = step.id === "verify" && !done && verificationPending(status);
    let statusKind = "upcoming";
    if (done) statusKind = "done";
    else if (index === activeIndex) statusKind = pending ? "pending" : "active";
    else statusKind = "locked";
    return { ...step, done, pending, statusKind };
  });

  const recommended = RECOMMENDED_STEPS.map(step => ({
    ...step,
    done: isDone(step.id, status)
  }));

  return {
    activeBranch,
    branchName: activeBranch?.name || "your shop",
    steps,
    recommended,
    // Recommended steps (equipment/staff/roles) are branch-scoped — only surface
    // them once a branch exists, else their CTAs have nowhere to deep-link.
    hasBranch: isDone("branch", status),
    requiredTotal: REQUIRED_STEPS.length,
    requiredDone: requiredDoneCount(status),
    allRequiredDone: allRequiredDone(status),
    // Exposed for the go-live step's sub-checks (branch on + non-empty menu).
    isActive: status.isActive === true,
    serviceMenuCount: status.serviceMenuCount || 0,
    isDevOverride
  };
};

// Dev-only: force a phase via ?onboarding=fresh|pending|progress|complete so
// every state can be screenshotted without touching backend data.
const devPresetStatus = () => {
  if (process.env.NODE_ENV === "production") return undefined;
  try {
    const key = new URLSearchParams(window.location.search).get("onboarding");
    return key && PRESETS[key] ? PRESETS[key] : undefined;
  } catch (e) {
    return undefined;
  }
};

export const useOnboardingStatus = () => {
  const { onboardingStatus, activeBranch } = useSelector(state => state.app);
  const override = devPresetStatus();
  return buildOnboardingStatus(
    override || onboardingStatus || {},
    activeBranch,
    !!override
  );
};

// Gates the side menu and routes while onboarding is incomplete. Fails open
// until the status loads — the axios interceptor + backend SubscriptionGuard
// stay authoritative, this only shapes navigation.
export const useOnboardingGate = () => {
  const { onboardingStatus } = useSelector(state => state.app);
  const status = devPresetStatus() || onboardingStatus;
  if (!status || allRequiredDone(status)) return { gating: false };
  const reached = reachedStepIds(status);
  const allowed = (map, key) =>
    key in map && (map[key] === null || reached.has(map[key]));
  return {
    gating: true,
    menuAllowed: key => allowed(MENU_STEP_GATE, key),
    routeAllowed: path => allowed(ROUTE_STEP_GATE, path)
  };
};
